import { useEffect, useCallback, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface FullScreenWrapperProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Portal-based full-screen overlay for widgets that need more real estate.
 * Renders over the entire viewport with a dark backdrop and close button.
 * Traps focus and handles Escape key.
 */
export function FullScreenWrapper({
  open,
  onClose,
  title,
  children,
  className,
}: FullScreenWrapperProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-daydra-bg/95 backdrop-blur-sm animate-fade-in">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-daydra-border shrink-0">
        {title && (
          <h2 className="text-sm font-semibold text-daydra-text truncate">
            {title}
          </h2>
        )}
        <button
          onClick={onClose}
          className="ml-auto flex items-center justify-center h-8 w-8 rounded-lg text-daydra-dim hover:text-daydra-text hover:bg-daydra-raised transition-colors"
          aria-label="Close full screen"
        >
          <X size={18} />
        </button>
      </div>

      {/* Content area */}
      <div className={cn("flex-1 overflow-auto", className)}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
